import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthHeader, LoaderDots } from 'components';
import AccountRecovery from 'components/AccountRecovery/AccountRecovery';
import { isValidDateOfBirth, validateFormat } from 'lib/Validations';
import { routeConstants } from 'routes';
import { browserHistory } from 'routes/Router';
import { createAccountRecoveryRequest } from 'store/AccountRecovery';
import { selectAccountRecovery } from 'store/AccountRecovery/selectors';
import * as ActionTypes from 'store/AccountRecovery/types';
import { selectAPIRequestErrors, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { formatDateForApi } from 'utils/date';
import AccountRecoveryEmailVerification from './EmailVerification';

const AccountRecoveryInit: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, ActionTypes.AccountRecovery.CREATE_ACCOUNT_RECOVERY_REQUEST));
  const accountRecovery = useSelector(selectAccountRecovery());
  const accountRecoveryErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, ActionTypes.AccountRecovery.CREATE_ACCOUNT_RECOVERY_REQUEST));

  // Redirect to login if we don't have an auth id
  if (!accountRecovery?.authId) {
    history.replace(routeConstants.public.login);
  }

  // Build validation schema
  let validationSchema = {};

  if (accountRecovery?.hasDob) {
    validationSchema = {
      ...validationSchema,
      dateOfBirth: Yup.string()
        .test('isValidDateOfBirth', 'Invalid date of birth', (dateOfBirth: string) => {
          if (validateFormat(dateOfBirth, 'MM-DD-YYYY')) {
            return isValidDateOfBirth(formatDateForApi(dateOfBirth));
          }
          return false;
        })
        .required(),
    };
  }

  if (accountRecovery?.hasPin) {
    validationSchema = {
      ...validationSchema,
      recoveryPin: Yup.string().required('A 4 digit code must be entered').min(4, 'A 4 digit code must be entered').max(4, 'A 4 digit code must be entered'),
    };
  }

  const validateField = (field: 'dateOfBirth' | 'recoveryPin') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, setFieldValue, getFieldProps, handleBlur, resetForm } = useFormik({
    initialValues: {
      dateOfBirth: '',
      recoveryPin: '',
    },
    onSubmit: () => {
      const payload = {
        ...(accountRecovery?.hasPin && values.recoveryPin && { recoveryPin: values.recoveryPin }),
        ...(accountRecovery?.hasDob && values.dateOfBirth && { dateOfBirth: formatDateForApi(values.dateOfBirth) }),
      };

      if (accountRecovery && accountRecovery.authId) dispatch(createAccountRecoveryRequest(accountRecovery.authId, payload, onSuccess));
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape(validationSchema),
  });

  const onSuccess = () => {
    setShowEmailVerification(true);
  };

  const onBack = () => {
    setShowEmailVerification(false);
  };

  useEffect(() => {
    resetForm();
  }, [showEmailVerification, dispatch, resetForm]);

  const handleOnBack = () => {
    resetForm();
    browserHistory.goBack();
  };

  if (!accountRecovery) return <LoaderDots />;

  return showEmailVerification && accountRecovery ? (
    <AccountRecoveryEmailVerification onBack={onBack} accountRecovery={accountRecovery} />
  ) : (
    <AccountRecovery
      authHeader={<AuthHeader data-cy="account-recovery-header" title="Account Recovery" subtitle="Complete the following to update the mobile number on your account." onBack={() => handleOnBack()} />}
      onSubmit={handleSubmit}
      validateField={validateField}
      setFieldValue={setFieldValue}
      getFieldProps={getFieldProps}
      isValid={isValid}
      dirty={dirty}
      values={values}
      errorMessages={accountRecoveryErrors?.errorMessages}
      formId={ActionTypes.AccountRecovery.CREATE_ACCOUNT_RECOVERY_REQUEST}
      errors={errors}
      isLoading={isLoading}
      handleBlur={handleBlur}
      hasDob={accountRecovery.hasDob}
      hasPin={accountRecovery.hasPin}
    />
  );
};

export default AccountRecoveryInit;
